import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route
} from "react-router-dom";

import { FC, lazy, Suspense } from "react";

import { LinearProgress } from "@mui/material";
import AppLayout from "./layouts/AppLayout";
import AuthLayout from "./layouts/AuthLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { brand, billing, enrichment } from "./auth";

const suspended = (lazyImport: ReturnType<typeof lazy>): FC => {
  const Component = lazyImport;
  return () => (
    <Suspense fallback={<LinearProgress />}>
      <Component />
    </Suspense>
  );
};

const DashboardPage = suspended(lazy(() => import("./pages/Dashboard")));
const LoginPage = suspended(lazy(() => import("./pages/Login")));
const AccountPage = suspended(lazy(() => import("./pages/Account")));
const BillingPage = suspended(lazy(() => import("./pages/Billing")));
const TrustIDSetupPage = suspended(lazy(() => import("./pages/TrustIDSetup")));
const BrandsPage = suspended(lazy(() => import("./pages/Brands")));
const BrandSettingsPage = suspended(lazy(() => import("./pages/BrandSettings")));
const ConnectionsPage = suspended(lazy(() => import("./pages/Connections")));
//TODO: Add Campaigns back when ready 
const CampaignsPage = lazy(() => import("./pages/Campaigns"));
const CampaignPage = suspended(lazy(() => import("./pages/ManagedCampaign")));
const RoleBasedRedirect = suspended(lazy(() => import("./components/RoleBasedRedirect")));
const BrandConfigPage = suspended(lazy(() => import("./pages/BrandConfig")));
const DataEnrichmentPage = suspended(lazy(() => import("./pages/DataEnrichment")));

// ========= DEPRECATED PAGES OR PAGES FOR THE LATEST RELEASES =========
// const AutomationRulesPage = suspended(lazy(() => import("./pages/AutomationRules")));
// const EarnRulesPage = suspended(lazy(() => import("./pages/EarnRules")));
// const CustomersPage = suspended(lazy(() => import("./pages/Customers")));
// const CustomerDetailsPage = suspended(lazy(() => import("./pages/Customers/details")));
// const HelpPage = suspended(lazy(() => import("./pages/Help")));
// const SettingsPage = suspended(lazy(() => import("./pages/Settings")));
// const StatusPage = suspended(lazy(() => import("./pages/Status")));
// const BrandConfigurationPage = suspended(lazy(() => import("./pages/BrandConfiguration")));

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AuthLayout />}>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<AppLayout />}>
        <Route index element={<RoleBasedRedirect />} />

        <Route path="/dashboard" element={
          <ProtectedRoute requiredPermission={brand.read.ReadListOfBrands}>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/:brandId" element={
          <ProtectedRoute requiredPermission={brand.read.ReadBrandById}>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/brands" element={<BrandsPage />} />
        <Route path="/brand-config" element={<BrandConfigPage />} />
        <Route path="/brand/:brandId" element={<BrandSettingsPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/billing" element={
          <ProtectedRoute requiredPermission={billing.read.GetBillingData} redirectTo="/dashboard">
            <BillingPage />
          </ProtectedRoute>
        } />
        <Route path="/trustid-setup" element={<TrustIDSetupPage />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/enrichment" element={
          <ProtectedRoute requiredPermission={enrichment.read.GetEnrichmentConfig}>
            <DataEnrichmentPage />
          </ProtectedRoute>
        } />
        <Route path="/data-enrichment" element={
          <ProtectedRoute requiredPermission={enrichment.read.GetEnrichmentConfig}>
            <DataEnrichmentPage />
          </ProtectedRoute>
        } />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/:id" element={<CampaignPage />} />

        {/* DEPRECATED PAGES OR PAGES FOR THE LATEST RELEASES  */}
        {/* 
          <Route path="/rules" element={<AutomationRulesPage />} />
          <Route path="/earn" element={<EarnRulesPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomerDetailsPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/brand" element={<BrandConfigurationPage />} />
         */}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  )
);
